import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProgressIndicator, ProgressStep, Button } from "@carbon/react";
import i18next from "../../i18n";
import axios from "../../axios-orders";
import { BIDDING_TYPES } from "../../utils";
import { Loader } from "../../components";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import BiddingTypeSelector from "./BiddingTypeSelector/BiddingTypeSelector";
import ServiceStepStatus from "./PriceDecrementPayment/ServiceDetailCard/ServiceStepStatus";
import { firstServiceGap, hasService } from "./serviceDetail";
import PriceDecrementPayment from "./PriceDecrementPayment/PriceDecrementPayment";
import MultipleMaterialsBiddingForm from "./MultipleMaterialsBiddingForm/MultipleMaterialsBiddingForm";
import MultipleBiddingSummary from "./MultipleBiddingSummary/MultipleBiddingSummary";
import StepMaterials from "./steps/StepMaterials";
import StepSuppliers from "./steps/StepSuppliers";
import { useDebounce, useFileInputUploader } from "../../hooks";
import { MaterialType } from "../../types";
import {
  useMaterial,
  useMaterials,
  useAllMaterials,
  useSearchMaterials,
} from "../../hooks/useMaterials";
import { queryKeys } from "../../hooks/queryKeys";
import { useNotification, useOrderInvitation, useUser } from "../../context";
import { useGetTags } from "../../hooks/useTags";
import { TagResponse } from "../../types";
import { useSites } from "../../hooks/useSites";
import InlineCreateSiteModal from "../OrderByRequest/InlineCreateSiteModal/InlineCreateSiteModal";
import { SiteResponse } from "../../types/site.type";
import { hasConsultationViolation } from "./generalDataStep";
import { actions, types, WIZARD_STEPS } from "./orderConstants";
import { seedMaterialLine } from "./orderSupplierState";
import { useOrderBuilder } from "./useOrderBuilder";
import { useStepGating } from "./useStepGating";
import { useOrderSubmission } from "./useOrderSubmission";
import "./order.scss";

const Order = () => {
  const { openNotification, openError } = useNotification();
  const { invites, cleanUserInvites, createOrderInvitation } =
    useOrderInvitation();
  const { user, companyList } = useUser();
  const showCompany = (companyList?.length ?? 0) > 1;
  const { data: sites } = useSites(user?.company?.id ?? "");
  const [siteModalLineId, setSiteModalLineId] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchMaterialsPage, setSearchMaterialsPage] = useState(0);
  const [materialsForBiddingPage, setMaterialsForBiddingPage] = useState(0);
  const [materialsPageSize, setMaterialsPageSize] = useState(10);
  const [selectedTags, setSelectedTags] = useState<TagResponse[]>([]);
  const [materialType, setMaterialType] = useState<MaterialType>(
    MaterialType.ALL
  );
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeStep, setActiveStep] = useState(1);
  const [open, setOpen] = useState(false);

  const builder = useOrderBuilder();
  const {
    ordersToSubmit,
    setOrdersToSubmit,
    generalBidData,
    setGeneralBidData,
    selectedType,
    setSelectedType,
    selectedComplexType,
    isAllSelected,
    errorMessage,
    handleCheckboxChange,
    handleSupplierSelection,
    handleSelectAll,
    handleSelectJointAll,
    handleOrdersToSubmit,
    assignSiteToLine,
    handleAddressChange,
    applyUseOnceAddress,
    handleApplyDatesToAll,
    handleAddLineFile,
    handleRemoveLineFile,
    handleServiceDetailChange,
    handlePriceDecrementChanges,
    handleSimpleSupplyPeriod,
    handleSupplyPeriod,
  } = builder;

  const { disabled, setDisabled } = useStepGating({
    activeStep,
    ordersToSubmit,
    selectedType,
    selectedComplexType,
    generalBidData,
  });

  const {
    isSubmitting,
    isCreatingOrder,
    isCreatingMultipleOrder,
    creatingIndividualOrders,
    onSubmit,
    onError,
  } = useOrderSubmission({
    ordersToSubmit,
    generalBidData,
    selectedType,
    selectedComplexType,
    user,
    invites,
    cleanUserInvites,
    createOrderInvitation,
    openNotification,
    openError,
    navigate,
    closeDialog: () => setOpen(false),
  });

  const { debouncedSearchTerm } = useDebounce(searchTerm, 500);
  const tagIds = selectedTags.map((t) => t.id);
  const {
    data: uploadedSpecificationFileUrl,
    mutate: uploadFile,
    isPending: isUploading,
    isSuccess: isUploadSuccess,
  } = useFileInputUploader();

  const materialId = searchParams.get("id");

  const { mutate: onGetBiddingSuppliers, data: suppliers } = useMutation({
    mutationFn: (materialIds) =>
      axios.post(`/buyerSeller/materials`, materialIds),
  });

  const { data: material } = useMaterial(materialId || "");

  // The order-creation material picker stays scoped to the current user's own materials (the owner
  // filter that broadened the /materials LIST is not wanted here), so pass the caller as ownerIds.
  const pickerOwnerIds = user?.id ? [user.id] : [];

  const { data: foundMaterials } = useSearchMaterials(
    debouncedSearchTerm,
    searchMaterialsPage,
    tagIds,
    selectedCompanyIds,
    pickerOwnerIds,
    materialsPageSize
  );

  const { data: materials, isLoading: isLoadingMaterials } = useMaterials(
    queryKeys.MATERIALS,
    materialType,
    materialsForBiddingPage,
    tagIds,
    selectedCompanyIds,
    pickerOwnerIds,
    materialsPageSize
  );

  const { data: allMaterials, isLoading: isLoadingAllMaterials } =
    useAllMaterials(
      materialType,
      materialsForBiddingPage,
      tagIds,
      selectedCompanyIds,
      pickerOwnerIds,
      materialsPageSize
    );

  // Changing items-per-page returns to the first page of both the browse and search lists so the
  // Carbon Pagination's own range math stays honest.
  const handleMaterialsPageSizeChange = (size: number) => {
    setMaterialsPageSize(size);
    setMaterialsForBiddingPage(0);
    setSearchMaterialsPage(0);
  };

  const { data: availableTags } = useGetTags();

  const { handleSubmit } = useForm();

  const handleToggleChange = (newMaterialType: MaterialType) => {
    setMaterialType(newMaterialType);
    setMaterialsForBiddingPage(0);
    setOrdersToSubmit([]);
  };

  const handleTagChange = (tags: TagResponse[]) => {
    setSelectedTags(tags);
    setMaterialsForBiddingPage(0);
    setSearchMaterialsPage(0);
  };

  const toggleCompany = (companyId: string) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId]
    );
    setMaterialsForBiddingPage(0);
    setSearchMaterialsPage(0);
  };

  useEffect(() => {
    if (!material) return;
    setOrdersToSubmit((prev) =>
      prev.length > 0 ? prev : [seedMaterialLine(material)]
    );
    setMaterialType(material.category.type as unknown as MaterialType);
  }, [material, setOrdersToSubmit]);

  const handleNext = (step: number) => {
    if (step === 1) {
      setActiveStep((prevState: number) => prevState + 1);
      setDisabled(true);
    }
    if (activeStep === 2) {
      setActiveStep((prevState: number) => prevState + 1);
      setDisabled(true);
      const mat = ordersToSubmit?.map((m: any) => m.id);
      onGetBiddingSuppliers(mat as any);
    }
    if (activeStep === 3) setActiveStep((prevState: number) => prevState + 1);
    if (activeStep === 4) setActiveStep((prevState: number) => prevState + 1);
    if (activeStep === 5) setActiveStep((prevState: number) => prevState + 1);
    if (activeStep === 6) setOpen(true);
  };

  const handleSiteChange = (lineId: string, siteId: string) => {
    const site =
      (sites as SiteResponse[] | undefined)?.find((s) => s.id === siteId) ??
      null;
    assignSiteToLine(lineId, site);
  };

  const handleSiteCreated = (site: SiteResponse) => {
    if (siteModalLineId) assignSiteToLine(siteModalLineId, site);
    setSiteModalLineId(null);
  };

  const handleUseOnceAddress = ({ address }: { address: string }) => {
    if (!siteModalLineId) return;
    applyUseOnceAddress(siteModalLineId, address);
    setSiteModalLineId(null);
  };

  const handleSpecification = (e: any) => {
    const file = e?.target?.files;
    if (file) uploadFile(file[0]);
  };

  const step4Type =
    selectedComplexType?.value === BIDDING_TYPES.JOINT &&
    selectedType?.value === BIDDING_TYPES.INVERSE
      ? BIDDING_TYPES.INVERSE_JOINT
      : selectedComplexType?.value === BIDDING_TYPES.JOINT &&
          selectedType?.value === BIDDING_TYPES.NORMAL
        ? BIDDING_TYPES.JOINT
        : selectedType?.value;

  const isBusy =
    isCreatingOrder || isCreatingMultipleOrder || creatingIndividualOrders;

  return (
    <div>
      <PageHeader title={i18next.t("navigation.newTender")} />
      {isBusy ? (
        <div className="order-loader">
          <Loader />
        </div>
      ) : (
        <div className="cds-form-surface">
          <ProgressIndicator
            currentIndex={activeStep - 1}
            spaceEqually
            className="order-builder__steps"
          >
            {WIZARD_STEPS.map(({ id, labelKey }) => (
              <ProgressStep key={id} label={i18next.t(labelKey)} />
            ))}
          </ProgressIndicator>

          {activeStep === 1 && (
            <BiddingTypeSelector
              types={types}
              onChangeType={(e) => setSelectedType(e)}
              typeValue={selectedType}
            />
          )}
          {activeStep === 2 && (
            <StepMaterials
              materialType={materialType}
              onMaterialTypeChange={handleToggleChange}
              availableTags={availableTags}
              selectedTags={selectedTags}
              onTagsChange={handleTagChange}
              companyList={companyList}
              selectedCompanyIds={selectedCompanyIds}
              toggleCompany={toggleCompany}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              errorMessage={errorMessage}
              selectedComplexType={selectedComplexType}
              ordersToSubmit={ordersToSubmit}
              isLoading={isLoadingMaterials || isLoadingAllMaterials}
              allMaterials={allMaterials}
              materials={materials}
              foundMaterials={foundMaterials}
              materialsForBiddingPage={materialsForBiddingPage}
              setMaterialsForBiddingPage={setMaterialsForBiddingPage}
              searchMaterialsPage={searchMaterialsPage}
              setSearchMaterialsPage={setSearchMaterialsPage}
              pageSize={materialsPageSize}
              onPageSizeChange={handleMaterialsPageSizeChange}
              handleCheckboxChange={handleCheckboxChange}
            />
          )}
          {activeStep === 3 && (
            <StepSuppliers
              suppliers={suppliers?.data}
              selectedComplexType={selectedComplexType}
              isAllSelected={isAllSelected}
              onSelectAll={() => handleSelectAll(suppliers?.data)}
              onSelectJointAll={() => handleSelectJointAll(suppliers?.data)}
              ordersToSubmit={ordersToSubmit}
              invites={invites}
              handleSupplierSelection={handleSupplierSelection}
              handleOrdersToSubmit={handleOrdersToSubmit}
            />
          )}
          {activeStep === 4 && (
            <PriceDecrementPayment
              type={step4Type}
              materialsToBid={ordersToSubmit}
              handlePriceDecrementChanges={handlePriceDecrementChanges}
              handleSupplyPeriod={handleSimpleSupplyPeriod}
              sites={sites}
              handleSiteChange={handleSiteChange}
              handleAddressChange={handleAddressChange}
              onCreateSite={(lineId: string) => setSiteModalLineId(lineId)}
              onApplyDatesToAll={handleApplyDatesToAll}
              onAddLineFile={handleAddLineFile}
              onRemoveLineFile={handleRemoveLineFile}
              onServiceDetailChange={handleServiceDetailChange}
              showCompany={showCompany}
            />
          )}
          {activeStep === 5 && (
            <MultipleMaterialsBiddingForm
              type={selectedComplexType.value}
              actions={actions}
              materialsToBid={ordersToSubmit}
              isLoadingUpload={isUploading}
              isUploadSuccess={isUploadSuccess}
              generalBidData={generalBidData}
              setGeneralBidData={setGeneralBidData}
              handleSpecification={handleSpecification}
              uploadedFileUrl={uploadedSpecificationFileUrl}
              handleSupplyPeriod={handleSupplyPeriod}
              onApplyDatesToAll={handleApplyDatesToAll}
              consultationViolation={hasConsultationViolation(generalBidData)}
            />
          )}
          {activeStep === 6 && (
            <MultipleBiddingSummary
              orderSummary={ordersToSubmit}
              generalBidData={generalBidData}
              type={selectedType.value}
              showCompany={showCompany}
            />
          )}

          <div className="order-actions">
            {activeStep === 4 && hasService(ordersToSubmit) && (
              <ServiceStepStatus
                gap={firstServiceGap(ordersToSubmit)}
                complete={!disabled}
              />
            )}
            <Button
              kind="secondary"
              onClick={() =>
                setActiveStep((prevState: number) => prevState - 1)
              }
              disabled={activeStep === 1}
            >
              {i18next.t("cta.back")}
            </Button>
            <Button
              kind="primary"
              onClick={() => handleNext(activeStep)}
              disabled={disabled || isUploading}
            >
              {activeStep === 6
                ? i18next.t("cta.launch")
                : i18next.t("cta.next")}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={open}
        title={i18next.t("cta.launch")}
        body={i18next.t("order.question")}
        confirmLabel={i18next.t("cta.launch")}
        isPending={isCreatingMultipleOrder || isCreatingOrder || isSubmitting}
        onConfirm={handleSubmit(onSubmit, onError)}
        onCancel={() => setOpen(false)}
      />
      <InlineCreateSiteModal
        open={siteModalLineId !== null}
        setOpen={(value: boolean) => !value && setSiteModalLineId(null)}
        onCreated={handleSiteCreated}
        onUseOnceAddress={handleUseOnceAddress}
      />
    </div>
  );
};

export default Order;
